import React from 'react';
import { Autocomplete, TextField, Chip } from '@mui/material';
import type { FilterOption } from './FilterSelect';
import MuiLoader from '../utils/Loader';
import styles from './ui.module.css';

interface FilterMultiSelectProps {
  label: string;
  options: FilterOption[];
  value: FilterOption[];
  onChange: (value: FilterOption[]) => void;
  loading?: boolean;
  limitTags?: number;
  disabled?: boolean;
}

/**
 * FilterSelect's multi-pick sibling, for filters that narrow by several
 * values at once (e.g. a set of schemes). One chip style everywhere — pages
 * used to each pick their own chip colour for the same kind of control.
 */
const FilterMultiSelect: React.FC<FilterMultiSelectProps> = ({
  label,
  options,
  value,
  onChange,
  loading,
  limitTags = 2,
  disabled,
}) => (
  <Autocomplete
    multiple
    size="small"
    limitTags={limitTags}
    options={options}
    value={value}
    onChange={(_, next) => onChange(next)}
    getOptionLabel={opt => opt.label}
    isOptionEqualToValue={(opt, val) => opt.value === val.value}
    disabled={disabled}
    renderTags={(tagValue, getTagProps) =>
      tagValue.map((option, index) => (
        <Chip
          {...getTagProps({ index })}
          key={option.value}
          label={option.label}
          size="small"
          className={styles.filterChip}
        />
      ))
    }
    renderInput={params => (
      <TextField
        {...params}
        label={label}
        slotProps={{
          input: {
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <MuiLoader loaderSize={16} loaderColor="inherit" bgTransparency={0.15} />}
                {params.InputProps.endAdornment}
              </>
            ),
          },
        }}
      />
    )}
  />
);

export default FilterMultiSelect;
